const API_KEY = process.env.REACT_APP_GROQ_API_KEY;

// Debug: Check if API key is loaded
if (!API_KEY) {
  console.warn('Groq API key not found. Make sure .env file exists and you restarted the dev server.');
} else {
  console.log('Groq API key loaded:', API_KEY.substring(0, 10) + '...');
}

const MEDC_CONTEXT = `You are MEDC BOT, a friendly and helpful virtual assistant for MEDCoop (Employees' Development Cooperative).

About MEDCoop:
- Established on October 29, 1997
- Registered with the Cooperative Development Authority (CDA) under Registration Number 6520-11009760
- Workforce of more than 2,500 personnel distributed across Luzon, Visayas, and Mindanao
- Provides manpower services including: Security Services, Janitorial Services, Messengerial Services, Utility Personnel, and General Services

Contact Information (IMPORTANT - Always provide these exact details when asked):
- Main Office Address: 3rd Floor Marcela Building, Palma Gil Street, Barangay 34-D, Davao City 8000
- Phone Numbers: (082) 285-1120 and (082) 224-6036
- Email: medabordo@medc.ph
- Website: www.medcoop.ph
- Office Hours: Monday to Saturday, 8:00 AM to 5:00 PM

Employee Benefits:
- Updated SSS Remittances
- PhilHealth Coverage
- Pag-IBIG Fund
- 13th Month Pay
- 5 Days Incentive Leave with Pay
- Employees Compensation
- Uniform Allowance
- Training and Development Programs
- Career Growth Opportunities

Requirements for Employment:
- NBI Clearance (original)
- Police Clearance (original)
- Barangay Clearance (original)
- Valid Government ID (photocopy)
- Updated Resume/CV
- Medical Certificate
- 2x2 ID Photo (2 pieces)
- 1x1 ID Photo (2 pieces)

How to Apply:
1. Visit the MEDC office at 3rd Floor Marcela Building, Palma Gil Street, Davao City OR apply online through the Membership page on the website
2. Fill out the application form completely
3. Submit all required documents
4. Wait for the interview schedule (HR will contact you)
5. Attend orientation if hired

Core Values: Integrity, Excellence, Service, Teamwork, Innovation

Guidelines for responses:
- Be friendly, professional, and helpful
- Give SPECIFIC and EXACT information when asked about application process, requirements, benefits, or contact details
- ALWAYS provide the actual phone numbers, email, and address when users ask for contact information
- Keep responses concise but informative
- If asked about job openings or specific positions, direct them to the Careers/Membership page or Services page for online application
- Always represent MEDCoop positively
- Do not discuss topics unrelated to MEDCoop, politely redirect to MEDC-related topics
- If you don't know something specific about MEDC, be honest and suggest they contact the office directly using the phone numbers provided`;

export const getGroqResponse = async (userMessage, language = 'english') => {
  if (!API_KEY) {
    console.error('Groq API not initialized. Check API key configuration.');
    return {
      success: false,
      text: language === 'tagalog' 
        ? 'Paumanhin, hindi pa nakaayos ang AI assistant. Pakisubukan ulit mamaya.'
        : language === 'cebuano'
        ? 'Pasayloa, wala pa ma-setup ang AI assistant. Palihug sulayi pag-usab unya.'
        : 'Sorry, the AI assistant is not configured yet. Please try again later.'
    };
  }

  const languageInstruction = {
    english: 'Respond in English.',
    tagalog: 'Respond in Tagalog/Filipino language.',
    cebuano: 'Respond in Cebuano/Bisaya language.'
  };

  const userPrompt = `${languageInstruction[language] || languageInstruction.english}

User message: ${userMessage}

Provide a helpful, concise response (2-4 sentences max unless more detail is needed):`;

  // Try different Groq models as fallbacks
  const models = [
    'llama-3.3-70b-versatile',
    'llama-3.1-8b-instant',
    'gemma2-9b-it'
  ];

  for (const model of models) {
    try {
      console.log('Trying Groq model:', model);
      
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        },
        body: JSON.stringify({
          model: model,
          messages: [
            {
              role: 'system',
              content: MEDC_CONTEXT
            },
            {
              role: 'user',
              content: userPrompt
            }
          ],
          temperature: 0.7,
          max_tokens: 512
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Groq model failed:', response.status, errorData);
        continue;
      }

      const data = await response.json();
      console.log('Groq response received:', data);
      
      const text = data.choices?.[0]?.message?.content;
      
      if (text) {
        return {
          success: true,
          text: text.trim()
        };
      }
    } catch (error) {
      console.log('Groq model error:', error.message);
      continue;
    }
  }

  // All models failed
  console.error('All Groq API models failed');
  return {
    success: false,
    text: language === 'tagalog' 
      ? 'Paumanhin, nagkaproblema sa pagproseso ng iyong mensahe. Pakisubukan ulit.'
      : language === 'cebuano'
      ? 'Pasayloa, adunay problema sa pagproseso sa imong mensahe. Palihug sulayi pag-usab.'
      : 'Sorry, there was an issue processing your message. Please try again.'
  };
};

// Check if a message should use AI or predefined responses
export const shouldUseAI = (message) => {
  const lowerMessage = message.toLowerCase();
  
  // Keywords that trigger predefined responses (faster, more reliable, more accurate)
  const predefinedKeywords = [
    // About MEDC
    'about', 'medc', 'company', 'story', 'tungkol', 'bahin',
    // Services
    'service', 'offer', 'provide', 'serbisyo',
    // Application process
    'apply', 'application', 'how to', 'paano', 'unsaon', 'mag-apply', 'pag-apply',
    'hire', 'work', 'trabaho', 'empleyo',
    // Requirements
    'requirement', 'document', 'clearance', 'need', 'bring', 'kailangan', 'dalhin', 'kinahanglan',
    // Benefits
    'benefit', 'salary', 'pay', 'sweldo', 'sahod', 'sss', 'philhealth', 'pag-ibig', '13th', 'leave',
    // Membership
    'member', 'join', 'career', 'job', 'sumali', 'mosalmot',
    // Contact
    'contact', 'reach', 'phone', 'email', 'address', 'location', 'makipag', 'kontaka'
  ];
  
  // Check if any predefined keyword is in the message
  const hasPredefinedKeyword = predefinedKeywords.some(keyword => 
    lowerMessage.includes(keyword)
  );
  
  // Use AI for messages that don't match predefined categories
  return !hasPredefinedKeyword;
};
